import React from 'react';

import Link from 'next/link';
import { Pie, PieChart } from 'recharts';

import { useMyPoints } from '@/hooks/query/loans/useMyPoints';
import { useTheme } from '@/hooks/useTheme';
import { USDDollarFormatter } from '@/utils/formatter';
import { cn } from '@/utils/helper';

type UserStatsListProps = {
  nettyWorth: number | JSX.Element;
  customFields: {
    id: number;
    txt: string;
    value: string | number | JSX.Element | undefined;
    url?: string;
  }[];
  isSideBarMenu?: boolean;
} & (
  | {
      paidCount: number;
      foreclosedCount: number;
    }
  | {
      nettyScore: number;
    }
);

const UserStatsList: React.FC<UserStatsListProps> = ({ nettyWorth, customFields, isSideBarMenu = true, ...props }) => {
  const { mode } = useTheme();

  const paidCount = 'paidCount' in props ? props.paidCount : null;
  const foreclosedCount = 'foreclosedCount' in props ? props.foreclosedCount : null;
  const nettyScore = 'nettyScore' in props ? props.nettyScore : null;
  const { data: userPoints } = useMyPoints();

  const data = [
    { name: 'Defaulted Loans', value: foreclosedCount, fill: `${mode === 'light' ? '#E4E6F1' : '#333333'}`, cornerRadius: 40 },
    { name: 'Repaid Loans', value: paidCount, fill: '#1160FF', cornerRadius: 0 },
  ];

  const totalLoans = paidCount ? paidCount + (foreclosedCount ?? 0) : null;
  const panels: {
    id: number;
    txt: string;
    value: string | number | JSX.Element | undefined;
    url?: string;
    preFix?: boolean;
    postFix?: boolean;
    unit?: string;
  }[] = [
    {
      id: 1,
      txt: 'NettyWorth',
      value: typeof nettyWorth === 'number' || typeof nettyWorth === 'string' ? USDDollarFormatter(nettyWorth || 0) : nettyWorth,
      preFix: true,
    },
    {
      id: 156,
      txt: 'NettyPoints',
      value: <span className='!text-primary-blue-600 font-space-grotesk font-bold text-base'>${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(userPoints?.totalPoints ?? 0)} Pts</span>,
    },
    {
      id: 2,
      txt: 'Repayment Rate',
      unit: '%',
      value: (
        <>
          {!isSideBarMenu && data && (
            <PieChart width={24} height={24} className='-rotate-90'>
              <Pie data={data} innerRadius={10} outerRadius={12} paddingAngle={0} strokeWidth={0} dataKey='value' />
            </PieChart>
          )}
          {((paidCount && totalLoans ? (paidCount / totalLoans) * 100 : nettyScore) || 0).toFixed(2)}
        </>
      ),
      postFix: true,
    },
    ...customFields,
  ];

  return (
    <ul className={cn(mode === 'dark' ? 'border-b-coal-100' : 'border-light-blue-grey', isSideBarMenu ? 'pb-4 ss:pb-6 lg:pb-0 max-lg:border-b flex flex-col gap-4 ss:gap-7 flex-1' : 'pt-[10px] flex flex-col')}>
      {panels.map(({ id, url, txt, value, preFix, postFix, unit }, idx) => {
        return (
          <li key={id} className={cn(isSideBarMenu ? '' : 'border-b px-6 py-[17px] max-h-[55px]', 'flex justify-between items-center gap-1')}>
            {url ? (
              <Link href={url} className='flex justify-between items-center gap-1 w-full'>
                <span className='text-sm ss:text-base/5 font-medium'>{txt}</span>
                <span className={cn(idx === 0 ? '!text-green-250' : '', isSideBarMenu ? 'text-sm ss:text-base/5 font-medium' : 'text-sm/5 font-normal font-space-mono', 'flex items-center gap-3')}>
                  {preFix && unit}
                  {value}
                  {postFix && unit}
                </span>
              </Link>
            ) : (
              <>
                <p className='text-sm ss:text-base/5 font-medium'>{txt}</p>
                <p className={cn(idx === 0 ? '!text-green-250' : '', isSideBarMenu ? 'text-sm ss:text-base/5 font-medium' : 'text-sm/5 font-normal font-space-mono', 'flex items-center gap-3')}>
                  {preFix && unit}
                  {value}
                  {postFix && unit}
                </p>
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default UserStatsList;
